import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import api from '../services/api';
import { defaultsFor } from './defaults';
import { SiteContentContext } from './siteContentContext';

/**
 * The website's copy, from the admin panel, with a real page underneath it.
 *
 * Three layers, in order: what the admin wrote for this language, then the
 * bundled defaults for this language, then Persian. The point of the last two
 * is that the site never renders an empty heading - a section nobody has
 * written yet shows shipped copy rather than a gap, and the visitor cannot
 * tell the difference.
 *
 * The fetch is not blocking. The page renders from defaults on the first frame
 * and swaps when the request lands, so a slow API costs a repaint rather than
 * a blank screen.
 */

/** Merges a written section over its default, field by field. */
function mergeSection(written, fallback) {
  if (!written || typeof written !== 'object') return fallback || {};
  const merged = { ...(fallback || {}) };

  for (const [key, value] of Object.entries(written)) {
    // An empty string is "cleared", not "written" - an admin who blanks a
    // field should see the default come back rather than an empty heading.
    if (typeof value === 'string' && value.trim() === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    merged[key] = value;
  }

  return merged;
}

export function SiteContentProvider({ children }) {
  const { i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? 'en' : 'fa';

  const [remote, setRemote] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    api
      .get('/site/content', { params: { locale } })
      .then((response) => {
        if (cancelled) return;
        setRemote(response.data?.content || {});
        setStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        // The bundled copy is a complete page, so a failed request is not an
        // error state the visitor needs to see - it is just an older page.
        setRemote(null);
        setStatus('fallback');
      });

    return () => {
      cancelled = true;
    };
  }, [locale]);

  const value = useMemo(() => {
    const fallback = defaultsFor(locale);
    const persian = defaultsFor('fa');
    const sections = new Set([
      ...Object.keys(persian),
      ...Object.keys(fallback),
      ...Object.keys(remote || {}),
    ]);

    const content = {};
    for (const name of sections) {
      content[name] = mergeSection(
        remote?.[name],
        mergeSection(fallback[name], persian[name]),
      );
    }

    return { content, status, locale };
  }, [remote, status, locale]);

  return (
    <SiteContentContext.Provider value={value}>
      {children}
    </SiteContentContext.Provider>
  );
}

export default SiteContentProvider;
